import { useEffect, useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, RefreshCw, Search, Check } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const SETTLEMENT_CYCLES = ["DAILY", "WEEKLY", "FORTNIGHTLY", "MONTHLY", "MULTI_MONTH"];

export const validateStep6 = (formData) => {
  const errors = {};
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!formData.email) errors.email = "Required";
  else if (!emailRe.test(formData.email)) errors.email = "Invalid email";
  if (!formData.password) errors.password = "Required";
  else if (formData.password.length < 6) errors.password = "Min 6 characters";
  if (
    (formData.partner_type === "REVENUE" || formData.partner_type === "EXECUTION") &&
    !formData.network_partner_id
  ) {
    errors.network_partner_id = "Required for Revenue/Execution partners";
  }
  return errors;
};

const TYPE_BADGE = {
  NETWORK: "bg-blue-100 text-blue-700 border-blue-200",
  REVENUE: "bg-purple-100 text-purple-700 border-purple-200",
  EXECUTION: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

export default function Step6Account({ formData, setFormData, errors }) {
  const [showPwd, setShowPwd] = useState(false);
  const [networkOptions, setNetworkOptions] = useState([]);
  const [networkSearch, setNetworkSearch] = useState("");
  const [networkOpen, setNetworkOpen] = useState(false);
  const [networkLoading, setNetworkLoading] = useState(false);
  const wrapperRef = useRef(null);

  const set = (k, v) => setFormData((p) => ({ ...p, [k]: v }));

  // Pre-fill email from Step 2 if Step 6 email empty
  useEffect(() => {
    if (!formData.email && formData.email !== "" && formData.contact_email) {
      set("email", formData.contact_email);
    }
  }, []);

  const needsNetworkLink =
    formData.partner_type === "REVENUE" || formData.partner_type === "EXECUTION";

  const fetchNetworks = async () => {
    if (!needsNetworkLink) return;
    setNetworkLoading(true);
    try {
      const token = localStorage.getItem("sendtoo_token");
      const r = await fetch(
        `${API}/api/v2/partners?type=NETWORK&status=active&page=1&page_size=200`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) {
        const d = await r.json();
        const items = d.items || d.partners || d || [];
        setNetworkOptions(Array.isArray(items) ? items : []);
      }
    } finally {
      setNetworkLoading(false);
    }
  };

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setNetworkOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const generatePassword = () => {
    const chars =
      "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#$%";
    let pwd = "";
    for (let i = 0; i < 12; i++) {
      pwd += chars[Math.floor(Math.random() * chars.length)];
    }
    set("password", pwd);
    setShowPwd(true);
    const copyPromise = navigator?.clipboard?.writeText
      ? navigator.clipboard.writeText(pwd)
      : Promise.reject(new Error("clipboard unavailable"));
    copyPromise
      .then(() => toast.success("Password copied to clipboard!"))
      .catch(() => toast.success("Password generated (clipboard unavailable)"));
  };

  const filteredNetworks = networkOptions.filter((p) => {
    const q = networkSearch.toLowerCase();
    if (!q) return true;
    return (
      (p.business_name || "").toLowerCase().includes(q) ||
      (p.partner_code || "").toLowerCase().includes(q)
    );
  });

  const selectedNetwork = networkOptions.find(
    (p) => p.id === formData.network_partner_id
  );

  // Auto-trigger when MULTI_MONTH cycle requires N
  const showMonthsN = formData.settlement_cycle === "MULTI_MONTH";

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6"
      data-testid="step6-account"
    >
      {/* LEFT */}
      <div className="space-y-7">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Account Setup</h2>
          <p className="text-sm text-slate-500">
            Configure login, commission, settlement and pricing rules
          </p>
        </div>

        {/* Login */}
        <Section title="Login Credentials">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Email" required error={errors.email} testid="login-email">
              <Input
                type="email"
                value={formData.email || ""}
                onChange={(e) => set("email", e.target.value)}
                placeholder="owner@business.com"
                data-testid="login-email-input"
              />
            </Field>

            <Field label="Password" required error={errors.password} testid="login-password">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPwd ? "text" : "password"}
                    value={formData.password || ""}
                    onChange={(e) => set("password", e.target.value)}
                    placeholder="Min 6 characters"
                    data-testid="login-password-input"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    data-testid="toggle-password-visibility"
                    aria-label="Toggle visibility"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generatePassword}
                  data-testid="auto-generate-password-btn"
                  className="whitespace-nowrap"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Auto-generate
                </Button>
              </div>
            </Field>
          </div>
        </Section>

        {/* Commission & Settlement */}
        <Section title="Commission & Settlement">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Commission %" testid="commission-pct">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={formData.commission_pct ?? ""}
                onChange={(e) =>
                  set(
                    "commission_pct",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                placeholder="10"
                data-testid="commission-pct-input"
              />
            </Field>
            <Field label="Settlement Cycle" testid="settlement-cycle">
              <select
                value={formData.settlement_cycle || ""}
                onChange={(e) => set("settlement_cycle", e.target.value)}
                className="w-full border border-slate-300 rounded h-10 px-2 text-sm bg-white"
                data-testid="settlement-cycle-select"
              >
                <option value="">Select…</option>
                {SETTLEMENT_CYCLES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            {showMonthsN && (
              <Field label="Every N months" testid="settlement-n">
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={formData.settlement_n_months ?? ""}
                  onChange={(e) =>
                    set(
                      "settlement_n_months",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="3"
                  data-testid="settlement-n-input"
                />
              </Field>
            )}
            <Field
              label="Settlement Day"
              helper="Day of week (1–7) or day of month (1–28)"
              testid="settlement-day"
            >
              <Input
                type="number"
                min="1"
                max="28"
                value={formData.settlement_day ?? ""}
                onChange={(e) =>
                  set(
                    "settlement_day",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                placeholder="1"
                data-testid="settlement-day-input"
              />
            </Field>
          </div>
        </Section>

        {/* Pricing Rules */}
        <Section title="Pricing Rules">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Inflation Min %" testid="inflation-min">
              <Input
                type="number"
                min="0"
                max="100"
                value={formData.inflation_min_pct ?? 25}
                onChange={(e) =>
                  set("inflation_min_pct", Number(e.target.value || 0))
                }
                data-testid="inflation-min-input"
              />
            </Field>
            <Field label="Inflation Max %" testid="inflation-max">
              <Input
                type="number"
                min="0"
                max="100"
                value={formData.inflation_max_pct ?? 100}
                onChange={(e) =>
                  set("inflation_max_pct", Number(e.target.value || 0))
                }
                data-testid="inflation-max-input"
              />
            </Field>
          </div>
          <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <strong>Note:</strong> 0% inflation is always allowed. 1% up to min% is
            blocked. Partners can inflate within min–max range only.
          </div>
        </Section>

        {/* Report Access */}
        <Section title="Report Access">
          <ToggleRow
            label="Partner can download commission report"
            checked={!!formData.report_download_partner}
            onChange={(v) => set("report_download_partner", v)}
            testid="report-download-partner"
          />
          <ToggleRow
            label="Staff can view this partner's report"
            checked={!!formData.report_download_staff}
            onChange={(v) => set("report_download_staff", v)}
            testid="report-download-staff"
          />
        </Section>

        {/* Wallet Settings */}
        <Section title="Wallet Settings">
          <Field label="Wallet Type" testid="wallet-type">
            <div className="flex">
              {["PREPAID", "CREDIT"].map((wt) => (
                <button
                  key={wt}
                  type="button"
                  onClick={() => set("wallet_type", wt)}
                  className={`flex-1 px-4 h-10 border text-xs font-semibold transition first:rounded-l last:rounded-r ${
                    (formData.wallet_type || "PREPAID") === wt
                      ? "bg-slate-800 text-white border-slate-800"
                      : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
                  }`}
                  data-testid={`wallet-type-${wt.toLowerCase()}`}
                >
                  {wt}
                </button>
              ))}
            </div>
          </Field>
          {(formData.wallet_type || "PREPAID") === "CREDIT" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <Field label="Credit Limit (₹)" testid="credit-limit">
                <Input
                  type="number"
                  min="0"
                  value={formData.credit_limit ?? ""}
                  onChange={(e) =>
                    set(
                      "credit_limit",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="50000"
                  data-testid="credit-limit-input"
                />
              </Field>
              <Field label="Credit Wallet Enabled" testid="credit-enabled">
                <ToggleRow
                  inline
                  checked={formData.credit_wallet_enabled !== false}
                  onChange={(v) => set("credit_wallet_enabled", v)}
                  testid="credit-enabled-toggle"
                />
              </Field>
            </div>
          )}
        </Section>

        {/* Network Linkage */}
        {needsNetworkLink && (
          <Section title="Network Linkage">
            <Field
              label="Parent Network Partner"
              required
              error={errors.network_partner_id}
              testid="network-partner"
            >
              <div className="relative" ref={wrapperRef}>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={
                      networkOpen
                        ? networkSearch
                        : selectedNetwork
                        ? `${selectedNetwork.partner_code || ""} — ${selectedNetwork.business_name || ""}`
                        : networkSearch
                    }
                    onFocus={() => {
                      if (!networkOptions.length) fetchNetworks();
                      setNetworkOpen(true);
                    }}
                    onChange={(e) => {
                      setNetworkSearch(e.target.value);
                      setNetworkOpen(true);
                    }}
                    placeholder="Search by name or partner code…"
                    className="pl-8"
                    data-testid="network-partner-input"
                  />
                </div>
                {networkOpen && (
                  <div className="absolute z-20 top-11 left-0 right-0 max-h-64 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg">
                    {networkLoading && (
                      <div className="px-3 py-2 text-xs text-slate-500">Loading…</div>
                    )}
                    {!networkLoading && filteredNetworks.length === 0 && (
                      <div className="px-3 py-2 text-xs text-slate-500">
                        No network partners found
                      </div>
                    )}
                    {filteredNetworks.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          set("network_partner_id", p.id);
                          setNetworkOpen(false);
                          setNetworkSearch("");
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between ${
                          formData.network_partner_id === p.id ? "bg-emerald-50" : ""
                        }`}
                        data-testid={`network-option-${p.id}`}
                      >
                        <span>
                          <span className="font-mono text-xs text-slate-500">
                            {p.partner_code || p.id?.slice(0, 6)}
                          </span>{" "}
                          — {p.business_name}
                        </span>
                        {formData.network_partner_id === p.id && (
                          <Check className="w-4 h-4 text-emerald-600" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Field>
            <ToggleRow
              label="Network partner earns commission on this partner's bookings"
              checked={!!formData.network_commission_enabled}
              onChange={(v) => set("network_commission_enabled", v)}
              testid="network-commission-enabled"
            />
            {formData.network_commission_enabled && (
              <Field label="Network Commission %" testid="network-commission-pct">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.network_commission_pct ?? ""}
                  onChange={(e) =>
                    set(
                      "network_commission_pct",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="2"
                  data-testid="network-commission-pct-input"
                />
              </Field>
            )}
          </Section>
        )}
      </div>

      {/* RIGHT — sticky summary */}
      <aside className="hidden lg:block">
        <div
          className="sticky top-6 bg-white border border-slate-200 rounded-lg p-4 shadow-sm"
          data-testid="summary-panel"
        >
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
            Live Summary
          </h3>
          <div className="space-y-2.5 text-sm">
            <span
              className={`inline-block text-[10px] font-bold px-2 py-1 rounded border ${
                TYPE_BADGE[formData.partner_type] || "bg-slate-100 text-slate-600 border-slate-200"
              }`}
            >
              {formData.partner_type || "TYPE"}
            </span>
            <div className="font-bold text-slate-800 text-base">
              {formData.business_name || "—"}
            </div>
            <div className="text-slate-600 text-xs">{formData.owner_name || "—"}</div>
            <div className="text-slate-500 text-xs">
              {formData.primary_mobile ? `+91 ${formData.primary_mobile}` : "—"}
              {formData.email ? ` | ${formData.email}` : ""}
            </div>
            <div className="text-slate-500 text-xs">
              {formData.city || "—"}, {formData.pincode || "—"}
            </div>
            <hr className="border-slate-100" />
            <SummaryRow
              label="Fleet"
              value={`${(formData.vehicles || []).filter((v) => v.vehicle_number || v.vehicle_type).length} vehicles (${(formData.vehicles || []).reduce((s, v) => s + Number(v.capacity_kg || 0), 0)}kg)`}
            />
            <SummaryRow
              label="Commission"
              value={
                formData.commission_pct !== "" && formData.commission_pct != null
                  ? `${formData.commission_pct}%`
                  : "—"
              }
            />
            <SummaryRow
              label="Settlement"
              value={
                formData.settlement_cycle
                  ? formData.settlement_cycle +
                    (formData.settlement_cycle === "MULTI_MONTH" && formData.settlement_n_months
                      ? ` (every ${formData.settlement_n_months}m)`
                      : "")
                  : "—"
              }
            />
            <SummaryRow
              label="Wallet"
              value={formData.wallet_type || "PREPAID"}
            />
            {selectedNetwork && (
              <SummaryRow
                label="Network"
                value={`${selectedNetwork.partner_code || ""} — ${selectedNetwork.business_name || ""}`}
              />
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between gap-2 text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 font-medium text-right">{value}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, required, helper, error, children, testid }) {
  return (
    <div data-testid={`field-${testid}`}>
      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
      {helper && !error && (
        <p className="text-[11px] text-slate-400 mt-1">{helper}</p>
      )}
      {error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testid, inline }) {
  return (
    <label
      className={`flex items-center ${inline ? "" : "justify-between py-2"} gap-3 cursor-pointer`}
      data-testid={testid}
    >
      {label && <span className="text-sm text-slate-700">{label}</span>}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
          checked ? "bg-emerald-600" : "bg-slate-300"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
            checked ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    </label>
  );
}
